import type { AxiosResponse } from 'axios'
import type { RowSelectionState, ColumnDef, CellContext } from '@tanstack/vue-table'
import { UploadIcon } from 'lucide-vue-next'
import { Checkbox } from '~/components/ui/checkbox'
import { Button } from '~/components/ui/button'
import BarsArrowUp from '~/components/BarsArrowUp.vue'
import ShadDatePicker from '~/components/ShadDatePicker.vue'
import InfiniteScrollDropdown from '~/components/InfiniteScrollDropdown.vue'
import TextInput from '~/components/TextInput.vue'

export const createColumns = (
  isInEditMode: Ref<boolean>,
  newSubmission: Ref<Partial<NewSubmissionTemplateData>>,
  rowSelection: Ref<RowSelectionState>,
  assetNameQuery: Ref<string>,
  assetsPageNumber: Ref<number>,
  listAssetsPromise: Ref<() => Promise<AxiosResponse<PaginatedResponse<OptionItem>>>>,
  rawDataKeys: ComputedRef<string[]>,
  onViewDocuments: (documents: EsgMetricRawData['supporting_documents']) => void,
  templateColumns: Ref<Array<EsgEntryTemplateColumn>>,
): ColumnDef<EsgSubmissionTemplateData>[] => [
  {
    accessorKey: 'activity_date',
    id: 'activity_date',
    minSize: 240,
    maxSize: 280,
    sortingFn: (a, b) => {
      const { $moment } = useNuxtApp()
      const dateA = $moment(a.original.activity_date, 'YYYY-MM-DD')
      const dateB = $moment(b.original.activity_date, 'YYYY-MM-DD')

      if (dateA.isValid() && dateB.isValid()) {
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
      }

      // fallback to string comparison if dates are invalid
      return new Date(a.original.activity_date).getTime() < new Date(b.original.activity_date).getTime() ? -1 : 1
    },
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('div', { class: 'flex justify-between items-center' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'activity date'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ]),
        isInEditMode.value && h('div', { class: 'pb-2' }, [
          h(ShadDatePicker, {
            'onUpdate:modelValue': value => newSubmission.value.activity_date = value,
          }),
        ]),
      ])
    },
    cell: ({ row, column }) => {
      const value = row.getValue(column.id)
      let formattedDate
      if (value) {
        formattedDate = String(value)
      }
      else {
        formattedDate = '-'
      }
      return h('div', { class: 'flex items-center gap-4' }, [
        isInEditMode.value && h(Checkbox, {
          'onUpdate:checked': (value: boolean) => {
            const rowId = row.original.id.toString()
            if (value) {
              rowSelection.value[rowId] = true
            }
            else {
              const newSelection = { ...rowSelection.value }
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete newSelection[rowId]
              rowSelection.value = newSelection
            }
            row.toggleSelected(!!value)
          },
        }),
        h('span', { class: 'text-md text-foreground truncate' }, formattedDate),
      ])
    },
  },
  {
    accessorKey: 'asset.name',
    id: 'asset',
    minSize: 240,
    maxSize: 280,
    sortingFn: (a, b) => {
      return a.original.asset.name.localeCompare(b.original.asset.name)
    },
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 !rotate-0' : ''

      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('div', { class: 'flex justify-between items-center' }, [
          h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'asset'),
          h(Button, {
            class: 'text-main-primary',
            variant: 'ghost',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc', true),
          }, () => h(BarsArrowUp, { customStyle: `size-5 rotate-180 ${sortIconStyling}` })),
        ]),
        isInEditMode.value && h('div', { class: 'pb-2' }, [
          h(InfiniteScrollDropdown, {
            'modelValue': assetNameQuery.value,
            'onUpdate:modelValue': value => assetNameQuery.value = value as string,
            'onUpdate:selected': (value) => {
              const selectedAsset = value as OptionItem
              newSubmission.value.asset = selectedAsset
              assetNameQuery.value = selectedAsset.name
            },
            'pageNumber': assetsPageNumber.value,
            'onUpdate:pageNumber': value => assetsPageNumber.value = value as number,
            'uniqueKey': 'user-assignment',
            'placeholder': 'Select asset',
            'fetchSuggestionsPromise': listAssetsPromise.value,
            'onFetchNextPage': () => assetsPageNumber.value++,
          }),
        ]),
      ])
    },
  },
  ...rawDataKeys.value.map(key => ({
    id: `raw_data.${key}`,
    minSize: 240,
    maxSize: 280,
    accessorFn: (row: EsgSubmissionTemplateData) => row.raw_data[key] || '-',
    header: () => {
      // Find the template column definition for this key
      const columnDef = templateColumns.value.find(col => col.name === key)
      const dataType = columnDef?.data_type || 'text'

      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, key.replace(/_/g, ' ')),
        isInEditMode.value && h('div', { class: 'pt-2' }, [
          dataType === 'date'
            ? h(ShadDatePicker, {
              'onUpdate:modelValue': (value: string | undefined) => {
                if (value) {
                  newSubmission.value.raw_data![key] = value
                }
              },
            })
            : dataType === 'number'
              ? h(TextInput, {
                type: 'text',
                onInput: (value) => {
                  // Only allow numeric input
                  const numericValue = value && !isNaN(value) ? Number(value) : ''
                  newSubmission.value.raw_data![key] = numericValue
                },
                placeholder: 'Enter number',
              })
              : h(TextInput, {
                onInput: value => newSubmission.value.raw_data![key] = value !== undefined ? value : '',
                placeholder: 'Enter text',
              }),
        ]),
      ])
    },
    cell: (context: CellContext<EsgSubmissionTemplateData, unknown>) => {
      const { $moment } = useNuxtApp()
      const value = context.getValue() as string | number
      const columnKey = context.column.id.replace('raw_data.', '')
      const columnDef = templateColumns.value.find(col => col.name === columnKey)

      // Format based on the column's defined data type
      if (columnDef?.data_type === 'date' && value) {
        try {
          // Use proper ISO format parsing with explicit format
          if (typeof value === 'string') {
            // Handle date strings in various formats
            const parsedDate = $moment(value, [
              'YYYY-MM-DD', // ISO format
              'DD/MM/YYYY', // European format
              'MM/DD/YYYY', // US format
              'YYYY/MM/DD', // Alternative ISO
            ], true)

            return h('span', {
              class: 'text-sm text-theme-text-black',
            }, parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : value)
          }
        }
        catch (e) {
          console.error('Error parsing date:', e)
          return h('span', { class: 'text-sm text-theme-text-black' }, value)
        }
      }

      return h('span', { class: 'text-sm text-theme-text-black' }, value)
    },
  })),
  {
    id: 'supporting_documents',
    maxSize: 140,
    header: () => {
      return h('div', { class: 'flex flex-col gap-2.5' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold text-center' }, 'Attachments'),
        h(Button, {
          class: 'text-theme-text-black text-sm',
          variant: 'link',
        }, () => h(UploadIcon)),
      ])
    },
    accessorKey: 'supporting_documents',
    cell: ({ row }) => {
      const documents = row.original.supporting_documents
      const lastUpdate = Date.now()

      const count = documents.length

      if (count === 0) {
        return h('span', { class: 'text-sm text-gray-400 text-center px-3 block' }, '0')
      }

      return h(Button, {
        class: 'text-theme-text-black text-center text-sm !py-0 w-full h-auto',
        variant: 'link',
        onClick: () => onViewDocuments?.(documents),
        key: `docs-${count}-${lastUpdate}`,
      }, () => `${count}`)
    },
  },
]
